// Shared season key-dates — the single client-facing source for the customer
// Season Calendar AND the Home hub's "important dates" block, so the two never
// drift. Thin adapter over the canon master `src/canon/season.json` (the one
// authored source; also projected to Firestore config/season + the committed
// functions/canon/canon-fallback.json so Cloud Functions read the same set).
//
// TWO blocking sets, and keeping them distinct is the whole point:
//   CLOSED_DATES    = closures (ranges inclusive) ∪ recital dates. The studio is
//                     dark; NOTHING may be scheduled, makeups included.
//   NO_LESSON_DATES = CLOSED_DATES ∪ flex days. No RECURRING weekly lesson lands
//                     here, and families see these days as closed.
// Flex days are exactly the difference: reserve capacity staff can drop makeup
// slots onto when a backlog builds, and days off when it doesn't. Folding them
// into CLOSED_DATES would block the very makeups they exist to absorb — so the
// makeup publication/redemption guards keep using isClosedDate, and only
// recurring expansion (src/utils/occurrenceExpansion.js) uses isNoLessonDate.
//
// The enumeration logic here is mirrored — by necessity of the src/ ↔ functions/
// package split — in functions/seasonDates.js, and a parity test locks the two
// against this same season.json.

import season from '../canon/season.json';

export const SEASON_LABEL = season.seasonLabel;

// Recitals as customer-facing events ({ name, date, location }). No required
// rehearsal this season (not in the 2026-27 calendar).
export const RECITALS = (season.recitals || []).map((r) => ({
	name: r.name,
	date: r.date,
	location: r.location || null,
}));

// Studio closures / breaks as human-readable ranges ({ name, start, end }) —
// drives the Season Calendar closures list.
export const CLOSURES = season.closures || [];

// Flex / reserve days ({ start, end }). No recurring lesson lands on one and
// families see them as closed; staff may still place makeups there.
export const FLEX_DAYS = season.flexDays || [];

// What a family is told a flex day is. They are never shown the word "flex" —
// to them it is simply a day the studio is closed, which is true.
export const FLEX_PUBLIC_NAME = 'Studio closed';

// First-lesson day per weekday cohort ({ mon: 'YYYY-MM-DD', ... }).
export const FIRST_LESSON_BY_WEEKDAY = season.firstLessonByWeekday || {};

// The weekday keys in the order a human reads a week, so callers iterating the
// cohorts render Monday-first instead of whatever order the JSON happens to
// carry. Object key order is stable in practice but is not a thing to depend on
// in display code.
const WEEKDAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

// Last regular lesson day of the season. Recurring slots carry it as endDate so
// a season's grid stops instead of recurring into the next one forever.
export const SEASON_END = season.seasonEnd || null;

// ---- tuition ---------------------------------------------------------------
//
// The school-year rate for THIS season, and the ONLY place the public site
// should read a tuition figure from. Before this existed the number was written
// out by hand in five places across three files (FAQ, /policies, and three spots
// on /register), so a rate change meant finding all five — which is precisely
// how a site comes to quote two different prices on two different pages.
//
// Scope note, and it matters: these constants drive COPY, not billing. Live
// parent billing runs through MyMusicStaff, so changing the canon changes what a
// family READS, never what they are CHARGED. The MMS-side rate is a separate,
// manual step.

export const TUITION = season.tuition || {};

// Pre-formatted for display so no caller re-implements currency formatting and
// drifts on the comma. USD, whole dollars — CYV tuition has never had cents.
const usd = (n) => (typeof n === 'number' ? `$${n.toLocaleString('en-US')}` : '');

export const TUITION_SEASON_TOTAL = TUITION.seasonTotal ?? null;
export const TUITION_MONTHLY = TUITION.installmentAmount ?? null;
export const TUITION_INSTALLMENTS = TUITION.installments ?? null;

export const TUITION_SEASON_TOTAL_LABEL = usd(TUITION.seasonTotal);
export const TUITION_MONTHLY_LABEL = usd(TUITION.installmentAmount);

// ---- late fee ---------------------------------------------------------------
//
// Same single-source discipline as the tuition figures above, and for the same
// reason: a fee quoted one way on /policies and another way in an email is the
// version a family argues with. Every number a family reads comes from here.
//
// LATE_FEE_EFFECTIVE_FROM is load-bearing rather than decorative. The policy was
// decided 2026-08-30, AFTER the 2026-08-21 returning-family welcome email had
// already gone out without it, so the first month every billed family has been
// told about in advance is October. Moving this date earlier means disclosing it
// to every billed family first — see season.json's description for the full rule.

export const LATE_FEE = TUITION.lateFee || {};

export const LATE_FEE_AMOUNT = LATE_FEE.amountDollars ?? null;
export const LATE_FEE_AMOUNT_LABEL = usd(LATE_FEE.amountDollars);
export const LATE_FEE_GRACE_DAYS = LATE_FEE.graceDays ?? null;
export const LATE_FEE_PAUSE_AFTER_DAYS = LATE_FEE.pauseLessonsAfterDays ?? null;
export const LATE_FEE_RELEASE_AFTER_DAYS = LATE_FEE.releaseSlotAfterDays ?? null;
export const LATE_FEE_EFFECTIVE_FROM = LATE_FEE.effectiveFrom || null;

// "October 1, 2026" — the effective date spelled the way the copy reads it.
// Built from the parts rather than `new Date(str)`: that constructor parses a
// bare YYYY-MM-DD as UTC midnight, which renders as the day before in every US
// timezone. Same trap as the date-input rule in CLAUDE.md.
export const LATE_FEE_EFFECTIVE_LABEL = (() => {
	const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(LATE_FEE_EFFECTIVE_FROM || '');
	if (!m) return '';
	const [, y, mo, d] = m;
	return new Date(Number(y), Number(mo) - 1, Number(d)).toLocaleDateString(
		'en-US',
		{ month: 'long', day: 'numeric', year: 'numeric' }
	);
})();

// "September–June" — the installment window, spelled the way the copy reads it.
export const TUITION_SCHEDULE_LABEL =
	TUITION.firstPaymentMonth && TUITION.lastPaymentMonth
		? `${TUITION.firstPaymentMonth}–${TUITION.lastPaymentMonth}`
		: '';

// ---- date helpers ----------------------------------------------------------

// Parse a 'YYYY-MM-DD' string as a LOCAL midnight date (no UTC shift).
const parseLocal = (s) => {
	const [y, m, d] = s.split('-').map(Number);
	return new Date(y, m - 1, d);
};

const startOfDay = (dt) => {
	const d = new Date(dt);
	d.setHours(0, 0, 0, 0);
	return d;
};

const pad2 = (n) => String(n).padStart(2, '0');
const ymd = (dt) => `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;

// A canon 'YYYY-MM-DD' as human copy ("Monday, August 31, 2026"). Parsed as
// LOCAL midnight, so a negative-offset time zone never renders the day before.
//
// This lives here rather than in each page because the alternative is what
// /register did until 2026-08-07: two spots typing "Monday, August 31, 2026" by
// hand, correct on the day they were written and drifting the moment the season
// rolls. Same reasoning as the tuition constants above — a date a family reads
// is copy, and copy comes from canon.
export const formatSeasonDate = (dateStr, opts = {}) =>
	dateStr
		? parseLocal(dateStr).toLocaleDateString('en-US', {
				weekday: 'long',
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				...opts,
		  })
		: '';

// The seven first-lesson cohorts in week order, pre-formatted for display:
// [{ key: 'mon', weekday: 'Monday', date: 'YYYY-MM-DD', label: 'Month D, YYYY' }]
// — the date shown is whatever canon carries, never a fixed one.
// Weekday cohorts with no canon date are dropped rather than rendered blank.
export const firstLessonCohorts = () =>
	WEEKDAY_ORDER.map((key) => {
		const date = FIRST_LESSON_BY_WEEKDAY[key];
		if (!date) return null;
		return {
			key,
			date,
			weekday: formatSeasonDate(date, {
				year: undefined,
				month: undefined,
				day: undefined,
			}),
			label: formatSeasonDate(date, { weekday: undefined }),
		};
	}).filter(Boolean);

// Inclusive list of 'YYYY-MM-DD' between start and end (each parsed as local
// midnight so DST never shifts a day).
const expandRange = (startStr, endStr) => {
	const out = [];
	const end = parseLocal(endStr);
	for (let d = parseLocal(startStr); d <= end; d.setDate(d.getDate() + 1)) {
		out.push(ymd(d));
	}
	return out;
};

// Whole days from `from` to `dateStr` (>= 0 for today/future).
export const daysUntil = (dateStr, from = new Date()) =>
	Math.round((parseLocal(dateStr) - startOfDay(from)) / 86400000);

// How many days a customer schedule view has to span to reach the LAST lesson
// of the season. The customer surfaces used to hardcode 60 days, a number sized
// for "a family claiming in early August looks at a fall schedule starting Aug
// 31" — i.e. for the claim moment, not for the season. That bound is what a
// parent hits when they try to cancel a lesson in December: the occurrence is
// never expanded, so it cannot be rendered and cannot be cancelled. The window
// now runs through SEASON_END and each surface decides how much of it to show
// at once.
//
// Floored at the old 60 so reach can never regress below today's behavior —
// notably once `from` passes SEASON_END (between seasons, when only single-date
// makeup slots exist) the difference goes negative.
export const seasonLookaheadDays = (from = new Date()) =>
	Math.max(60, SEASON_END ? daysUntil(SEASON_END, from) + 1 : 0);

// ---- closed-days blocking set ----------------------------------------------

// The set of every date on which no lesson is scheduled/rendered: closure
// ranges (inclusive) + recital days. MIRRORED in functions/seasonDates.js.
export const enumerateClosedDates = (s = season) => {
	const set = new Set();
	(s.closures || []).forEach((c) => {
		expandRange(c.start, c.end).forEach((d) => set.add(d));
	});
	(s.recitals || []).forEach((r) => {
		if (r.date) set.add(r.date);
	});
	return set;
};

// Built once at module load — occurrenceExpansion consults this per grid cell,
// so it must be O(1). Rebuilding would only matter if season.json changed at
// runtime, which it never does (build-time import).
export const CLOSED_DATES = enumerateClosedDates();

// Is the studio closed to lessons on this 'YYYY-MM-DD'?
export const isClosedDate = (dateStr) => CLOSED_DATES.has(dateStr);

// ---- why a day is closed (marker source) -----------------------------------

// isClosedDate answers WHETHER a day is closed; the calendar surfaces also need
// WHY, to render a marker. Returns null for an open day, else the reason:
//   { type: 'recital', name, location, date }        (an attendable event)
//   { type: 'closure', name, start, end }            (holiday / break)
//   { type: 'flex',    name, start, end }            (reserve — see below)
// Recitals take precedence: a recital date reads as the event, not "closed".
//
// A `flex` day carries the family-facing name "Studio closed" because that is
// what it IS to a family — no lesson happens. Staff surfaces branch on the type
// to say "Reserve — makeups only" instead, because staff CAN schedule there.
// One helper, two audiences, no second source of truth.
export const closureInfoFor = (dateStr) => {
	const recital = RECITALS.find((r) => r.date === dateStr);
	if (recital) {
		return { type: 'recital', name: recital.name, location: recital.location, date: recital.date };
	}
	const closure = CLOSURES.find((c) => dateStr >= c.start && dateStr <= c.end);
	if (closure) {
		return { type: 'closure', name: closure.name, start: closure.start, end: closure.end };
	}
	const flex = FLEX_DAYS.find((f) => dateStr >= f.start && dateStr <= f.end);
	if (flex) {
		return { type: 'flex', name: FLEX_PUBLIC_NAME, start: flex.start, end: flex.end };
	}
	return null;
};

// ---- flex / reserve days ---------------------------------------------------

// Flex days are reserve capacity: no recurring weekly lesson lands on one, and
// families see them as closed — but staff may still place a makeup slot there,
// which is the entire reason they are not simply closures. If a season's makeup
// backlog never materializes, they are days off.
export const FLEX_DATES = (() => {
	const set = new Set();
	FLEX_DAYS.forEach((f) => expandRange(f.start, f.end).forEach((d) => set.add(d)));
	return set;
})();

export const isFlexDay = (dateStr) => FLEX_DATES.has(dateStr);

// ---- no-lesson set (recurring blocking + the family-facing "closed" view) ---

// CLOSED_DATES ∪ FLEX_DATES. Pure, so the functions/ parity test can compare
// enumerations rather than module-load side effects.
export const enumerateNoLessonDates = (s = season) => {
	const set = enumerateClosedDates(s);
	(s.flexDays || []).forEach((f) => {
		expandRange(f.start, f.end).forEach((d) => set.add(d));
	});
	return set;
};

export const NO_LESSON_DATES = enumerateNoLessonDates();

// Does NO recurring weekly lesson happen on this 'YYYY-MM-DD'? This is the
// predicate for recurring expansion and for the family-facing calendar. It is
// deliberately NOT the predicate for makeup publication/redemption — those keep
// using isClosedDate, so makeups can still land on a flex day.
export const isNoLessonDate = (dateStr) => NO_LESSON_DATES.has(dateStr);

// ---- Home hub / Season Calendar surfaces -----------------------------------

// A flat, chronological list of the season's dated key events (recitals) as
// { key, type, title, subtitle, date }. Rehearsals/on-call are retired.
export const seasonKeyDates = () =>
	RECITALS
		.map((r) => ({
			key: r.name,
			type: 'recital',
			title: r.name,
			subtitle: r.location || '',
			date: r.date,
		}))
		.sort((a, b) => a.date.localeCompare(b.date));

// The next `limit` key dates from `from` onward, each tagged with daysAway.
// Empty when nothing is upcoming — callers render a calm empty state.
export const upcomingSeasonDates = (from = new Date(), limit = 3) =>
	seasonKeyDates()
		.filter((e) => daysUntil(e.date, from) >= 0)
		.slice(0, limit)
		.map((e) => ({ ...e, daysAway: daysUntil(e.date, from) }));

// Human "in N days" / "today" / "tomorrow" label for a days-away count.
export const daysAwayLabel = (n) => {
	if (n <= 0) return 'today';
	if (n === 1) return 'tomorrow';
	if (n < 7) return `in ${n} days`;
	if (n < 14) return 'in 1 week';
	return `in ${Math.round(n / 7)} weeks`;
};

// ---- important dates for lesson surfaces (recitals + closures + flex) -------
//
// seasonKeyDates()/upcomingSeasonDates() above are recital-only (the Home hub's
// original scope). The schedule list and the month grid also need CLOSURES, so
// these two return both, discriminated by `type`. One entry per closure RANGE
// (not per day). Each entry carries `start`/`end` ('YYYY-MM-DD'), sorted by
// start; recitals collapse to start === end === the recital date.
//
// FLEX ranges are included and typed `flex`, carrying the family-facing name.
// A family must see a flex day as closed — otherwise their schedule shows a gap
// with no explanation, which reads as a bug rather than a day off. Staff
// surfaces branch on `type === 'flex'` to say "Reserve — makeups only".

const recitalEntry = (r) => ({
	type: 'recital',
	name: r.name,
	location: r.location || null,
	start: r.date,
	end: r.date,
});

const closureEntry = (c) => ({
	type: 'closure',
	name: c.name,
	location: null,
	start: c.start,
	end: c.end,
});

const flexEntry = (f) => ({
	type: 'flex',
	name: FLEX_PUBLIC_NAME,
	location: null,
	start: f.start,
	end: f.end,
});

const byStart = (a, b) => a.start.localeCompare(b.start);

// Every dated entry a lesson surface may need to interleave, in one place so
// the window/upcoming helpers below can't drift apart.
const allImportantEntries = () => [
	...RECITALS.map(recitalEntry),
	...CLOSURES.map(closureEntry),
	...FLEX_DAYS.map(flexEntry),
];

// Recitals + closure ranges that OVERLAP the [from, from+days] window, sorted by
// start. Powers the customer schedule interleave.
export const importantDatesInWindow = (from = new Date(), days = 60) => {
	const fromStr = ymd(startOfDay(from));
	const toStr = ymd(new Date(startOfDay(from).getTime() + days * 86400000));
	return allImportantEntries()
		.filter((e) => e.start <= toStr && e.end >= fromStr)
		.sort(byStart);
};

// The next `limit` important dates (recitals + closures) still current as of
// `from` — a closure counts as upcoming until its end date passes. Each tagged
// with daysAway (to its start; negative if already in progress). Powers the
// Home dashboard's important-dates block.
export const upcomingImportantDates = (from = new Date(), limit = 3) => {
	const fromStr = ymd(startOfDay(from));
	return allImportantEntries()
		.filter((e) => e.end >= fromStr)
		.sort(byStart)
		.slice(0, limit)
		.map((e) => ({ ...e, daysAway: daysUntil(e.start, from) }));
};
